import type { Cart, Money } from "src/types/ctgraphql.d";
import { getCartItemsCount, getLineItemsTotal } from "src/utils/ct/cart-helper";
import { cartHasFreeShipping } from "src/utils/ct/free-shipping";

export type CartStoreState = {
  cart: Cart | null;
  id: string;
  version: number;
  totalItems: number;
  lineItemsTotal: Money | null;
  hasFreeShipping: boolean;
  shouldCreateNewCart: boolean;
  loading: boolean;
  hasLoaded: boolean;
};

const INITIAL_STATE: CartStoreState = {
  cart: null,
  id: "",
  version: 0,
  totalItems: 0,
  lineItemsTotal: null,
  hasFreeShipping: false,
  shouldCreateNewCart: false,
  loading: false,
  hasLoaded: false,
};

let state: CartStoreState = INITIAL_STATE;

let refetchFn: (() => Promise<Cart | null>) | null = null;

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

export const cartStore = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
  },
  getSnapshot: () => state,
  getServerSnapshot: () => INITIAL_STATE,

  setCart: (cart: Cart | null | undefined) => {
    const incomingVersion = (cart?.version as number) || 0;

    if (cart && cart.id === state.id && incomingVersion < state.version) {
      return;
    }

    state = {
      ...state,
      cart: cart || null,
      id: cart?.id || "",
      version: incomingVersion,
      totalItems: getCartItemsCount(cart?.lineItems || []),
      lineItemsTotal: getLineItemsTotal(cart?.lineItems || []),
      hasFreeShipping: cartHasFreeShipping(cart),
      hasLoaded: true,
    };

    emit();
  },

  setLoading: (loading: boolean) => {
    if (state.loading === loading) {
      return;
    }

    state = { ...state, loading };

    emit();
  },

  setShouldCreateNewCart: (shouldCreateNewCart: boolean) => {
    if (state.shouldCreateNewCart === shouldCreateNewCart) {
      return;
    }

    state = { ...state, shouldCreateNewCart };

    emit();
  },

  registerRefetch: (fn: (() => Promise<Cart | null>) | null) => {
    refetchFn = fn;
  },

  refetch: async (): Promise<Cart | null> => {
    if (!refetchFn) {
      return state.cart;
    }

    return refetchFn();
  },

  reset: () => {
    state = { ...INITIAL_STATE };

    emit();
  },
};
